import type { FilterDraft } from './analysis-commands';
import type { FieldCatalog, FieldKind } from './field-catalog';
import type { Rejection } from './rejection';
import type { WorkspaceCommand } from './workspace-reducer';
import type { WorkspaceDocument } from './workspace-document';

/** Elemento que se arrastra. Solo contiene identificadores: nunca datos. */
export type DragSource =
  | { readonly type: 'field'; readonly fieldId: string }
  | {
      readonly type: 'slot-field';
      readonly cardId: string;
      readonly kind: FieldKind;
      readonly index: number;
    }
  | { readonly type: 'card'; readonly cardId: string };

/** Zona donde se suelta. */
export type DropTarget =
  | { readonly type: 'canvas' }
  | { readonly type: 'card'; readonly cardId: string }
  | {
      readonly type: 'slot';
      readonly cardId: string;
      readonly kind: FieldKind;
      readonly index?: number;
    }
  | { readonly type: 'filters'; readonly cardId: string }
  | { readonly type: 'card-position'; readonly cardId: string };

export type DropResolution =
  | { readonly ok: true; readonly command: WorkspaceCommand }
  | { readonly ok: false; readonly rejection: Rejection };

const refuse = (message: string, code: Rejection['code'] = 'unsupported-drop'): DropResolution => ({
  ok: false,
  rejection: { code, message },
});

/** Filtro inicial al soltar un campo en la zona de filtros (el usuario lo edita despues). */
export function initialFilterDraft(fieldId: string, kind: FieldKind): FilterDraft {
  return kind === 'dimension'
    ? { target: { kind: 'dimension', dimensionId: fieldId }, operator: 'is-not-null', values: [] }
    : { target: { kind: 'measure', measureId: fieldId }, operator: 'gt', values: [0] };
}

/**
 * Traduce un arrastre en un comando del workspace, o lo rechaza con un motivo legible.
 * Las combinaciones no previstas se rechazan: un drop invalido nunca modifica el estado.
 * La validacion final (duplicados, limites, coherencia con el modelo) la hace el reducer.
 */
export function resolveDrop(
  source: DragSource,
  target: DropTarget,
  document: WorkspaceDocument,
  catalog: FieldCatalog,
): DropResolution {
  const hasCard = (cardId: string): boolean => document.cards.some((card) => card.id === cardId);

  if (source.type === 'field') {
    const field = catalog.get(source.fieldId);
    if (!field) return refuse('El campo no existe en el modelo.', 'unknown-field');
    switch (target.type) {
      case 'canvas':
        return { ok: true, command: { type: 'add-card', fieldIds: [field.id] } };
      case 'card':
        if (!hasCard(target.cardId)) return refuse('La tarjeta no existe.', 'unknown-card');
        return {
          ok: true,
          command: { type: 'add-field', cardId: target.cardId, fieldId: field.id },
        };
      case 'slot':
        if (!hasCard(target.cardId)) return refuse('La tarjeta no existe.', 'unknown-card');
        if (field.kind !== target.kind) {
          return refuse(
            field.kind === 'measure'
              ? `"${field.name}" es una medida: sueltela en Medidas.`
              : `"${field.name}" es una dimension: sueltela en Dimensiones.`,
            'kind-mismatch',
          );
        }
        return {
          ok: true,
          command: {
            type: 'add-field',
            cardId: target.cardId,
            fieldId: field.id,
            expectedKind: target.kind,
            ...(target.index === undefined ? {} : { position: target.index }),
          },
        };
      case 'filters':
        if (!hasCard(target.cardId)) return refuse('La tarjeta no existe.', 'unknown-card');
        return {
          ok: true,
          command: {
            type: 'add-filter',
            cardId: target.cardId,
            draft: initialFilterDraft(field.id, field.kind),
          },
        };
      case 'card-position':
        return refuse('Suelte el campo sobre una visualizacion o en el area vacia del canvas.');
    }
  }

  if (source.type === 'slot-field') {
    if (target.type !== 'slot' || target.cardId !== source.cardId) {
      return refuse('Los campos solo se reordenan dentro de su propia visualizacion.');
    }
    if (target.kind !== source.kind) {
      return refuse(
        source.kind === 'measure'
          ? 'Una medida no puede moverse a Dimensiones.'
          : 'Una dimension no puede moverse a Medidas.',
        'kind-mismatch',
      );
    }
    const card = document.cards.find((candidate) => candidate.id === source.cardId);
    if (!card) return refuse('La tarjeta no existe.', 'unknown-card');
    const size = source.kind === 'dimension' ? card.dimensions.length : card.measures.length;
    const to = Math.min(target.index ?? size - 1, size - 1);
    return {
      ok: true,
      command: {
        type: 'move-field',
        cardId: source.cardId,
        kind: source.kind,
        from: source.index,
        to,
      },
    };
  }

  if (target.type !== 'card-position' && target.type !== 'card') {
    return refuse('Las visualizaciones solo se reordenan dentro del canvas.');
  }
  const from = document.cards.findIndex((card) => card.id === source.cardId);
  const to = document.cards.findIndex((card) => card.id === target.cardId);
  if (from < 0 || to < 0) return refuse('La tarjeta no existe.', 'unknown-card');
  return { ok: true, command: { type: 'move-card', from, to } };
}
